import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CartSummary = ({ 
  cart, 
  cartTotal, 
  cartItemCount, 
  onUpdateQuantity, 
  onRemoveItem, 
  showCart, 
  setShowCart 
}) => {
  const deliveryFee = cartTotal > 25 ? 0 : 3.99;
  const tax = cartTotal * 0.08;
  const finalTotal = cartTotal + deliveryFee + tax;

  if (!showCart) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-warm-lg w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-primary-100">
          <div>
            <h2 className="font-headline text-xl font-bold text-text-primary">
              Your Order
            </h2>
            <p className="text-text-secondary">
              {cartItemCount} {cartItemCount === 1 ? 'item' : 'items'}
            </p>
          </div>
          <button
            onClick={() => setShowCart(false)}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-primary-50 transition-organic"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Cart Items */}
        <div className="max-h-96 overflow-y-auto p-6 space-y-4">
          {cart.map(item => (
            <div key={item.cartId} className="flex items-start space-x-4 p-4 bg-surface rounded-lg">
              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                <Image
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-text-primary text-sm truncate">
                  {item.name}
                </h3>
                
                {item.customItems && (
                  <p className="text-xs text-text-secondary mb-1">
                    Custom plate with {item.customItems.length} items
                  </p>
                )}
                
                {item.customizations && (
                  <div className="text-xs text-text-secondary mb-2">
                    {item.customizations.extraSauce && <span className="mr-2">Extra Sauce</span>}
                    {item.customizations.extraSpice && <span>Extra Spice</span>}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onUpdateQuantity(item.cartId, item.quantity - 1)}
                      className="w-6 h-6 rounded-full bg-primary-100 text-primary flex items-center justify-center hover:bg-primary-200 transition-organic"
                    >
                      <Icon name="Minus" size={12} />
                    </button>
                    <span className="w-6 text-center text-sm font-medium">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => onUpdateQuantity(item.cartId, item.quantity + 1)}
                      className="w-6 h-6 rounded-full bg-primary-100 text-primary flex items-center justify-center hover:bg-primary-200 transition-organic"
                    >
                      <Icon name="Plus" size={12} />
                    </button>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-primary">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                    <button
                      onClick={() => onRemoveItem(item.cartId)}
                      className="text-error hover:text-error-600 transition-organic"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="p-6 border-t border-primary-100 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">Subtotal</span>
            <span className="font-medium text-text-primary">${cartTotal.toFixed(2)}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">
              Delivery Fee {cartTotal > 25 && <span className="text-success-600">(Free!)</span>}
            </span>
            <span className="font-medium text-text-primary">
              ${deliveryFee.toFixed(2)}
            </span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">Tax</span>
            <span className="font-medium text-text-primary">${tax.toFixed(2)}</span>
          </div>
          
          <div className="border-t border-primary-100 pt-3">
            <div className="flex items-center justify-between">
              <span className="font-headline text-lg font-semibold text-text-primary">
                Total
              </span>
              <span className="font-headline text-xl font-bold text-primary">
                ${finalTotal.toFixed(2)}
              </span>
            </div>
          </div>

          {cartTotal < 25 && (
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="Truck" size={16} className="text-warning-600" />
                <span className="text-sm text-warning-700">
                  Add ${(25 - cartTotal).toFixed(2)} more for free delivery!
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-t border-primary-100 space-y-3">
          <Link
            to="/order-online-streamlined-experience"
            className="w-full btn-conversion text-center block"
            onClick={() => setShowCart(false)}
          >
            Proceed to Checkout
          </Link>
          
          <button
            onClick={() => setShowCart(false)}
            className="w-full bg-primary-100 text-primary font-medium py-3 px-4 rounded-lg hover:bg-primary-200 transition-organic"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartSummary;